import type { IconSvgElement } from "@hugeicons/react";
import {
  Cancel01Icon,
  PaymentSuccess01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { ReportHeader } from "views/applications-id-report/components/ReportHeader";
import { useAuthContext } from "providers/Auth";
import {
  ApplicantStatus,
  ApplicationStatus,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { Tabs } from "shared/components/BaseTabs";
import { getDisabledRerunTooltip } from "../utils";
import { useCombinedIncomeContext } from "../providers/useCombinedIncome";
import {
  INCOMPLETE_APPLICATION_STATUSES,
  INCOMPLETE_VERIFICATION_STATUSES
} from "../constants";

export const ReportHeaderSlice = () => {
  const {
    applicant,
    applicationId,
    applicantId,
    applicationStatus,
    applicationMeta,
    activeVerification,
    currentlySelectedVerificationId,
    setCurrentlySelectedVerificationId,
    activeVerificationId,
    activeCombinedIncomeVerification,
    selectedCombinedIncomeVerification,
    pendingReverifyRequests,
    isReadOnlyVerification: isReadOnly,
    verificationStatus,
    verificationTabs,
    isCancelingRerun,
    isRerunning,
    isRequestingNewIncome,
    isCancelingNewIncomeRequest,
    reportModals: {
      setShowRerunModal,
      setShowCancelRerunModal,
      setShowRequestNewIncomeModal,
      setShowCancelNewIncomeRequestModal
    }
  } = useCombinedIncomeContext();
  const { permissions } = useAuthContext();

  const isVerificationIncomplete =
    INCOMPLETE_VERIFICATION_STATUSES.includes(verificationStatus) &&
    INCOMPLETE_APPLICATION_STATUSES.includes(
      applicationMeta.data?.status as ApplicationStatus
    );

  const activeVerificationStatus =
    activeCombinedIncomeVerification?.status ?? verificationStatus;
  const isActiveVerificationInFlight = (
    [
      VerificationStatus.Pending,
      VerificationStatus.Processing,
      VerificationStatus.Started,
      VerificationStatus.Submitted
    ] as VerificationStatus[]
  ).includes(activeVerificationStatus);
  const isActiveVerificationRerunInProgress =
    Boolean(activeCombinedIncomeVerification?.rerun_version) &&
    isActiveVerificationInFlight;

  // A verification added after the application was in flight
  // (role/unit/template change) is handled by the request_new_income /
  // cancel_new_income_request endpoints, not the rerun flow — the header
  // button switches to those instead. Once the verification finishes (report
  // exists, applicant declared "no income", etc.) it behaves like any other
  // combined income row: partner should be able to re-run it.
  const isPostCreatedVerificationActive =
    Boolean(activeVerification?.is_post_created) &&
    isActiveVerificationInFlight;
  // Applicant aws notified to finish the post_created verification
  const isPostCreatedVerificationInProgress =
    isPostCreatedVerificationActive &&
    applicationMeta.data?.status === ApplicationStatus.Started &&
    !activeVerification?.archived_at;

  const disabledRerunModalTooltip = getDisabledRerunTooltip({
    canEditIncome: permissions.canEditIncome,
    isReadOnly,
    isRerunInProgressOnActive: isActiveVerificationRerunInProgress,
    isApplicantSubmitted:
      applicant?.application_status === ApplicantStatus.Submitted,
    isViewingActive: currentlySelectedVerificationId === activeVerificationId,
    activeIsInFlight: isActiveVerificationInFlight,
    activeIsCanceled: activeVerificationStatus === VerificationStatus.Canceled,
    hasPendingReverify: pendingReverifyRequests.length > 0,
    isPostCreatedVerification: isPostCreatedVerificationActive
  });

  const shouldShowCancelRerun =
    isActiveVerificationRerunInProgress && !isReadOnly;

  const reportDate = isVerificationIncomplete
    ? undefined
    : activeVerification.updated_at;

  const getHeaderButtonProps = (): {
    onClick: () => void;
    text: string;
    loading: boolean;
    hugeIcon?: IconSvgElement;
    strokeWidth?: number;
  } => {
    if (isPostCreatedVerificationActive && !isReadOnly) {
      if (isPostCreatedVerificationInProgress)
        return {
          onClick: () => setShowCancelNewIncomeRequestModal(true),
          text: "Cancel verification",
          loading: isCancelingNewIncomeRequest,
          hugeIcon: Cancel01Icon
        };
      return {
        onClick: () => setShowRequestNewIncomeModal(true),
        text: "Request verification",
        loading: isRequestingNewIncome,
        hugeIcon: PaymentSuccess01Icon,
        strokeWidth: 1.5
      };
    }
    if (shouldShowCancelRerun)
      return {
        onClick: () => setShowCancelRerunModal(true),
        text: "Cancel income re-run",
        loading: isCancelingRerun
      };
    return {
      onClick: () => setShowRerunModal(true),
      text: "Re-run report",
      loading: isRerunning || isCancelingRerun
    };
  };

  const headerButton = getHeaderButtonProps();

  return (
    <>
      <div className="-mt-2 mb-6 border-b-1 max-w-[90vw] md:max-w-[80vw] lg:max-w-[min(60vw,900px)] w-fit">
        {verificationTabs && verificationTabs.length > 1 && (
          <Tabs
            tabs={verificationTabs}
            active={currentlySelectedVerificationId!}
            onChange={setCurrentlySelectedVerificationId}
            size="sm"
            tabStyle="line"
          />
        )}
      </div>

      <ReportHeader
        applicationStatus={applicationStatus}
        applicationId={applicationId || "0"}
        title="Income history"
        applicantId={applicantId || 0}
        name={VerificationNames.CombinedIncome}
        reportDate={reportDate}
        hidePrint={isVerificationIncomplete}
        onRerunReport={headerButton.onClick}
        rerunRequirements={
          isPostCreatedVerificationActive
            ? undefined
            : selectedCombinedIncomeVerification?.rerun_requirements
        }
        disabledRerunModalTooltip={disabledRerunModalTooltip}
        reRunReportButtonText={headerButton.text}
        reRunReportButtonHugeIcon={headerButton.hugeIcon}
        reRunReportButtonHugeIconStrokeWidth={headerButton.strokeWidth}
        reRunLoading={headerButton.loading}
      />
    </>
  );
};
